"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Check, Copy, Download, Loader2, Share2, Smartphone, Trophy } from "lucide-react";

/**
 * Share dialog for a sales-win card — the team-chat sibling of the leaderboard's
 * ShareWinDialog, minus the template picker: there is exactly one template here
 * (t=policywin), pre-filled from the win, so the dialog is preview + ways out.
 *
 * The QR is the piece that actually gets wins onto Instagram: agents live in the
 * desktop CRM but Instagram lives on their phone. Scanning opens /share on the
 * phone with the image + caption, where the native share sheet takes over. Same
 * handoff page and QR generator the leaderboard (and Routes) already use.
 */
export interface SalesWinShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Relative /api/share-card?... path for the rendered card. */
  imgPath: string;
  caption: string;
}

export function SalesWinShareDialog({ open, onOpenChange, imgPath, caption }: SalesWinShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  // The QR must encode an ABSOLUTE url — it's scanned by a camera, not our app.
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);
  const handoffUrl = origin
    ? `${origin}/share?img=${encodeURIComponent(imgPath)}&cap=${encodeURIComponent(caption)}`
    : "";
  const qrSrc = handoffUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(handoffUrl)}`
    : "";

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard denied — the caption is visible to select manually */
    }
  };

  // Native share sheet is a PHONE flow. Desktop browsers either can't share files at
  // all or (macOS Chrome/Safari) open a share sheet nothing useful hangs off of — so on
  // desktop the button just downloads the PNG, and the QR is the road to Instagram.
  // iPadOS masquerades as Mac in the UA; maxTouchPoints tells them apart.
  const isMobile =
    typeof navigator !== "undefined" &&
    (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 2 && /Mac/.test(navigator.userAgent)));

  const nativeShare = async () => {
    setSharing(true);
    try {
      const blob = await (await fetch(imgPath)).blob();
      const file = new File([blob], "policy-win.png", { type: "image/png" });
      if (isMobile && navigator.canShare?.({ files: [file] })) {
        // Caption pre-copied — several platforms drop share text when files attach.
        try { await navigator.clipboard.writeText(caption); } catch { /* non-fatal */ }
        // Files only — iOS shares the title INSTEAD of the image otherwise.
        await navigator.share({ files: [file] });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "policy-win.png";
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch {
      /* user closed the sheet */
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            Share your win
          </DialogTitle>
          <DialogDescription>
            Your card is ready — post it, story it, send it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[1fr_240px]">
          {/* Preview — the actual PNG the route renders, so what they see is what posts. */}
          <div className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={imgPath} alt="Your win card" className="h-auto w-full" />
          </div>

          {/* Send-to-phone QR: desktop → phone → Instagram. */}
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 p-4 text-center dark:border-zinc-600">
            {qrSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={qrSrc} alt="QR code to open this card on your phone" width={160} height={160} />
            ) : null}
            <p className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
              <Smartphone className="h-3.5 w-3.5" /> Scan to open on your phone
            </p>
            <p className="text-[11px] leading-snug text-zinc-500">
              Post straight to Instagram or your story from there.
            </p>
          </div>
        </div>

        {/* Caption — visible so it can be selected manually if clipboard is denied. */}
        <p className="rounded-lg bg-zinc-50 px-3 py-2 text-xs leading-relaxed text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300">
          {caption}
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" asChild className="gap-2">
            <a href={imgPath} download="policy-win.png">
              <Download className="h-4 w-4" /> Download
            </a>
          </Button>
          <Button variant="outline" onClick={copyCaption} className="gap-2">
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy caption"}
          </Button>
          <Button onClick={nativeShare} disabled={sharing} className="gap-2">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
